/**
 * @typedef {{
 *  componentName: string | undefined,
 *  onMount: () => void | undefined,
 *  onUnmount: () => void | undefined,
 *  render: () => void | undefined
 * }} ComponentOptions
 */

/**
 * A basic Component class to manage lifecycle methods.
 */
class Component {
  /**
   * @param {ComponentOptions} options
   */
  constructor(options) {
    /** @type {string} */
    this.componentName = options.componentName ?? "Component";
    /** @type {() => void | null} */
    this.onMount = options.onMount ?? null;
    /** @type {() => void | null} */
    this.onUnmount = options.onUnmount ?? null;
    /** @type {() => void | null} */
    this.componentRender = options.render ?? null;

    this.mounted = false;
  }

  mount() {
    if (!this.mounted && this.onMount) {
      this.onMount();
      this.mounted = true;
    }
    return this;
  }

  unmount() {
    if (this.mounted && this.onUnmount) {
      this.onUnmount();
      this.mounted = false;
    }
    return this;
  }

  render() {
    this.mount();
    if (this.componentRender) {
      this.componentRender();
    }
    return this;
  }
}

/**
 * Static no-op component that can be returned when a component should not initialize
 * @returns {Component}
 */
Component.noop = () => {
  return new Component({
    componentName: "Noop",
    onMount: () => {},
    onUnmount: () => {},
    render: () => {},
  });
};

export default Component;
